import { MutableRefObject, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CoreTypography } from '@youscience/core';
import { ScholarshipCard as CardProps } from '@common-types';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';
import { NavigationOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/pagination';

import ActionButton from '@components/ActionButton';

import ScholarshipCard from '@components/ScholarshipCard';

import { CollegeLocation } from '@interfaces/location';

import { getFromSchoolLocation } from '@utils/breadcrumbs';

import useScholarshipListStore from '@store/AllScholarshipsStore';

import { ROUTES } from '@constants/ROUTES';

import ChevronLeftIcon from '@images/chevron-left.svg?react';
import ChevronRightIcon from '@images/chevron-right.svg?react';

import { PAGE_TABS_DATA } from '@constants/scholarships';
import {
  sxStyles,
  StyledSliderInner,
  StyledSliderHeader,
  StyledSwiper,
  StyledNavigationButtonsWrap,
  StyledNavigationLink,
} from './RelatedScholarships.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

const SWIPER_DATA = {
  PAGINATION: {
    clickable: true,
  },
  BREAKPOINTS: {
    1180: {
      slidesPerView: 2,
    },
    576: {
      slidesPerView: 1,
    },
  },
};

interface CollegesNearbyProps {
  scholarships: CardProps[];
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  schoolName?: string;
}

export const RelatedScholarships = (props: CollegesNearbyProps) => {
  const { scholarships, scrollToRef, schoolName = '' } = props;

  const { setScholarshipFilters } = useScholarshipListStore((state) => state);

  const location: CollegeLocation = useLocation();
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const cameFrom = getFromSchoolLocation(location.pathname);

  const handleBeforeInit = (swiper: Swiper) => {
    // eslint-disable-next-line no-param-reassign
    swiper.params.navigation = {
      ...(swiper.params.navigation as NavigationOptions),
      prevEl: navigationPrevRef.current,
      nextEl: navigationNextRef.current,
    };
  };

  const handleExploreMore = () => {
    setScholarshipFilters({ keywords: schoolName });
  };

  if (!scholarships.length) return null;

  return (
    <StyledSliderInner data-testid="relatedScholarships">
      <SectionLinkOffset id={PageSectionIds['scholarship']} ref={scrollToRef} />

      <StyledSliderHeader>
        <CoreTypography sx={sxStyles.sliderTitle} variant="h4" component="h3">
          Search for scholarship
        </CoreTypography>

        <StyledNavigationButtonsWrap>
          <StyledNavigationLink
            to={`${ROUTES.SCHOLARSHIPS}#${PAGE_TABS_DATA.allScholarships.hash}`}
            state={cameFrom}
            onClick={handleExploreMore}
          >
            Explore more
          </StyledNavigationLink>

          <div ref={navigationPrevRef}>
            {scholarships.length > 1 && (
              <ActionButton sx={sxStyles.paginationButton}>
                <ChevronLeftIcon />
              </ActionButton>
            )}
          </div>

          <div ref={navigationNextRef}>
            {scholarships.length > 1 && (
              <ActionButton sx={sxStyles.paginationButton}>
                <ChevronRightIcon />
              </ActionButton>
            )}
          </div>
        </StyledNavigationButtonsWrap>
      </StyledSliderHeader>

      <StyledSwiper
        spaceBetween={32}
        pagination={SWIPER_DATA.PAGINATION}
        navigation={{
          nextEl: navigationNextRef.current,
          prevEl: navigationPrevRef.current,
        }}
        modules={[Pagination, Navigation]}
        breakpoints={SWIPER_DATA.BREAKPOINTS}
        onBeforeInit={handleBeforeInit}
      >
        {scholarships.map((scholarship) => (
          <SwiperSlide key={scholarship.id}>
            {/* TODO add state for breadcrumbs */}
            <ScholarshipCard scholarship={scholarship} />
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </StyledSliderInner>
  );
};
